// TAPINTO® Editor Frontend - Text Component
// Version: 1.0
// Date: 2025-10-01

import React from 'react';
import { useNode } from '@craftjs/core';
import { ComponentProps } from '../../../types';

interface TextProps extends ComponentProps {
  text: string;
  fontSize: 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  fontWeight: 'normal' | 'medium' | 'semibold' | 'bold';
  textAlign: 'left' | 'center' | 'right' | 'justify';
  color: string;
  backgroundColor: string;
  padding: string;
  margin: string;
}

export function Text({
  text,
  fontSize,
  fontWeight,
  textAlign,
  color,
  backgroundColor,
  padding,
  margin,
  ...props
}: TextProps) {
  const {
    connectors: { connect, drag },
    isActive,
    isHovered,
  } = useNode((state) => ({
    isActive: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const fontSizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
  };

  const fontWeightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const textAlignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={`
        ${isActive ? 'ring-2 ring-primary-500' : ''}
        ${isHovered ? 'ring-2 ring-primary-300' : ''}
      `}
      style={{
        color,
        backgroundColor,
        padding,
        margin,
      }}
      {...props}
    >
      <p
        className={`
          ${fontSizeClasses[fontSize]}
          ${fontWeightClasses[fontWeight]}
          ${textAlignClasses[textAlign]}
        `}
      >
        {text}
      </p>
    </div>
  );
}

Text.craft = {
  displayName: 'Text Block',
  props: {
    text: 'This is a text block. Click to edit.',
    fontSize: 'base',
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#1F2937',
    backgroundColor: 'transparent',
    padding: '16px',
    margin: '0',
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
    canMoveOut: () => true,
  },
};
